export const CARD_TYPES = {
  invitation: 'INVITATION',
  invitationEnvelope: 'INVITATION_ENVELOPE',
  std: 'SAVE_THE_DATE',
  dstd: 'DIGITAL_SAVE_THE_DATE',
  ctd: 'CHANGE_THE_DATE',
  thankyou: 'THANK_YOU',
  menu: 'MENU',
  program: 'PROGRAM',
  place: 'PLACE',
  holiday: 'HOLIDAY',
  details: 'DETAILS',
  enclosure: 'ENCLOSURE',
  rsvp: 'RSVP',
  rsvpEnvelope: 'RSVP_ENVELOPE',
  envelope: 'ENVELOPE',
  matchingSuites: 'MATCHING_SUITES',
  weddingShower: 'WEDDING_SHOWER_INVITATION',
  tableNumber: 'TABLE_NUMBER',
  paperAddOns: 'PAPER_ADD_ONS',
  bellyBand: 'BELLY_BAND',
  vellumJacket: 'VELLUM_JACKET',
  waxSeal: 'WAX_SEAL',
} as const;

const ENVELOPE_CARD_TYPES = [CARD_TYPES.invitationEnvelope, CARD_TYPES.rsvpEnvelope] as const;

export type EnvelopCardTypes = typeof ENVELOPE_CARD_TYPES[number];
export const isEnvelopeCardType = (type?: string): type is EnvelopCardTypes => {
  if (!type) return false;
  return ENVELOPE_CARD_TYPES.includes(type as EnvelopCardTypes);
};

export type FullCardType = typeof CARD_TYPES[keyof typeof CARD_TYPES];
export type LeadCardType = Exclude<FullCardType, EnvelopCardTypes>;

export const isCardType = (type?: string): type is FullCardType => {
  if (!type) return false;
  return (Object.values(CARD_TYPES) as string[]).includes(type);
};

export const isLeadCardType = (type?: string): type is LeadCardType => {
  if (!type) return false;
  return isCardType(type) && !isEnvelopeCardType(type);
};

// Paper add ons lead card type array used for api requests
export const PAPER_ADD_ONS_LEAD_CARD_TYPES = [
  CARD_TYPES.bellyBand,
  CARD_TYPES.vellumJacket,
  CARD_TYPES.waxSeal,
] as const;

export type PaperAddOnLeadCardType = typeof PAPER_ADD_ONS_LEAD_CARD_TYPES[number];
export const isPaperAddOnLeadCardType = (type?: string): type is PaperAddOnLeadCardType => {
  if (!type) return false;
  return PAPER_ADD_ONS_LEAD_CARD_TYPES.includes(type as PaperAddOnLeadCardType);
};

// Paper add ons lead card types plus PAPER_ADD_ONS (FE exculsive type) used for FE purposes only
const ALL_PAPER_ADD_ONS_CARD_TYPES: FullCardType[] = [
  ...PAPER_ADD_ONS_LEAD_CARD_TYPES,
  CARD_TYPES.paperAddOns,
];
export type AllPaperAddOnsCardType = typeof ALL_PAPER_ADD_ONS_CARD_TYPES[number];
export const isAllPaperAddOnsCardType = (type?: string): type is AllPaperAddOnsCardType => {
  if (!type) return false;
  return ALL_PAPER_ADD_ONS_CARD_TYPES.includes(type as AllPaperAddOnsCardType);
};

// Card types whose PDP and confirmation pages should include the paper add-ons upsell carousel
const CARD_TYPES_WITH_ADD_ONS: FullCardType[] = [
  ...PAPER_ADD_ONS_LEAD_CARD_TYPES,
  CARD_TYPES.std,
  CARD_TYPES.weddingShower,
  CARD_TYPES.invitation,
];
export type CardTypeWithAddOns = typeof CARD_TYPES_WITH_ADD_ONS[number];
export const isCardTypeWithAddOns = (type?: string): type is CardTypeWithAddOns => {
  if (!type) return false;
  return CARD_TYPES_WITH_ADD_ONS.includes(type as CardTypeWithAddOns);
};

const CARD_TYPES_WITH_ADDITIONAL_SHAPES = [
  CARD_TYPES.invitation,
  CARD_TYPES.std,
  CARD_TYPES.menu,
  CARD_TYPES.program,
  CARD_TYPES.holiday,
  CARD_TYPES.weddingShower,
  CARD_TYPES.tableNumber,
] as const;

export type CardTypeWithAdditionalShapes = typeof CARD_TYPES_WITH_ADDITIONAL_SHAPES[number];
export const isCardTypeWithAdditionalShapes = (
  type?: string
): type is CardTypeWithAdditionalShapes => {
  if (!type) return false;
  return CARD_TYPES_WITH_ADDITIONAL_SHAPES.includes(type as CardTypeWithAdditionalShapes);
};

const TYPES_WITHOUT_SILHOUETTE_OPTIONS = [
  CARD_TYPES.ctd,
  CARD_TYPES.place,
  CARD_TYPES.paperAddOns,
] as const;

export type CardTypeWithoutSilhouetteOptions = typeof TYPES_WITHOUT_SILHOUETTE_OPTIONS[number];
export const isCardTypeWithoutSilhouetteOptions = (
  type?: string
): type is CardTypeWithoutSilhouetteOptions => {
  if (!type) return false;
  return TYPES_WITHOUT_SILHOUETTE_OPTIONS.includes(type as CardTypeWithoutSilhouetteOptions);
};

const CARD_TYPES_WITH_SAMPLES = [CARD_TYPES.std, CARD_TYPES.invitation] as const;
export type CardTypeWithSamples = typeof CARD_TYPES_WITH_SAMPLES[number];
export const isCardTypeWithSamples = (type?: string): type is CardTypeWithSamples => {
  if (!type) return false;
  return CARD_TYPES_WITH_SAMPLES.includes(type as CardTypeWithSamples);
};

const CARD_TYPES_WITH_FREE_ENVELOPES = [
  CARD_TYPES.invitation,
  CARD_TYPES.std,
  CARD_TYPES.ctd,
  CARD_TYPES.thankyou,
  CARD_TYPES.holiday,
  CARD_TYPES.rsvp,
  CARD_TYPES.weddingShower,
] as const;

export type FreeEnvelopeCardType = typeof CARD_TYPES_WITH_FREE_ENVELOPES[number];
export const isFreeEnvelopeCardType = (type?: string): type is FreeEnvelopeCardType => {
  if (!type) return false;
  return CARD_TYPES_WITH_FREE_ENVELOPES.includes(type as FreeEnvelopeCardType);
};

// QR CODES
const CARD_TYPES_WITH_QR_CODE_ON_BACK = [
  CARD_TYPES.std,
  CARD_TYPES.invitation,
  CARD_TYPES.enclosure,
  CARD_TYPES.details,
  CARD_TYPES.weddingShower,
] as const;

export type CardWithQrCodeOnBack = typeof CARD_TYPES_WITH_QR_CODE_ON_BACK[number];
export const isCardWithQrCodeOnBack = (type?: string): type is CardWithQrCodeOnBack => {
  if (!type) return false;
  return CARD_TYPES_WITH_QR_CODE_ON_BACK.includes(type as CardWithQrCodeOnBack);
};

const CARD_TYPES_WITH_QR_CODE_ON_FRONT = [
  CARD_TYPES.rsvp,
  CARD_TYPES.enclosure,
  CARD_TYPES.details,
] as const;

export type CardTypeWithQrCodeOnFront = typeof CARD_TYPES_WITH_QR_CODE_ON_FRONT[number];
export const isCardTypeWithQrCodeOnFront = (type?: string): type is CardTypeWithQrCodeOnFront => {
  if (!type) return false;
  return CARD_TYPES_WITH_QR_CODE_ON_FRONT.includes(type as CardTypeWithQrCodeOnFront);
};

export type CardTypeWithQrCode = CardWithQrCodeOnBack & CardTypeWithQrCodeOnFront;
export const isCardTypeWithQrCode = (type?: string): type is CardTypeWithQrCode => {
  return isCardWithQrCodeOnBack(type) || isCardTypeWithQrCodeOnFront(type);
};

export const CARD_TYPES_WITH_PHOTO_BADGE = [
  CARD_TYPES.std,
  CARD_TYPES.invitation,
  CARD_TYPES.weddingShower,
];
export type CardWithPhotoBadge = typeof CARD_TYPES_WITH_PHOTO_BADGE[number];

export const isCardWithPhotoBadge = (type?: string): type is CardWithPhotoBadge => {
  if (!type) return false;
  return CARD_TYPES_WITH_PHOTO_BADGE.includes(type as CardWithPhotoBadge);
};

// ! Deprecated?
const ENABLED_PROOF_ORDER_CARD_TYPES: FullCardType[] = [
  CARD_TYPES.invitation,
  CARD_TYPES.std,
  CARD_TYPES.ctd,
  CARD_TYPES.menu,
  CARD_TYPES.program,
  CARD_TYPES.enclosure,
  CARD_TYPES.thankyou,
  CARD_TYPES.holiday,
  CARD_TYPES.place,
];

export type EnabledProofOrderCardType = typeof ENABLED_PROOF_ORDER_CARD_TYPES[number];
export const isEnabledProofOrderCardType = (type?: string): type is EnabledProofOrderCardType => {
  if (!type) return false;
  return ENABLED_PROOF_ORDER_CARD_TYPES.includes(type as EnabledProofOrderCardType);
};

export type CardSettings = {
  type: LeadCardType;
  urlSlug: string | null;
  label: string;
  hasFoil: boolean;
  hasLetterpress: boolean;
  hasMotifs: boolean;
  hasShapes: boolean;
  hasMagnet: boolean;
  hasMultipleOrientations: boolean;
  hasThirdPartyDesigns: boolean;
  hasCustomPhoto: boolean;
  hasNoCustomPhoto: boolean;
  isVowRenewal: boolean;
  isLead: boolean;
  showPdpFreeAddressingModule: boolean | null;
  freeAddressingText: string | null;
  freeAddressingImage: string | null;
  showPdpCtdBanner: boolean | null;
  showPdpCovidBanner: boolean | null;
  showReviewStepCtdBanner: boolean | null;
};

export const CARD_TYPES_BEFORE_THE_DAY = [CARD_TYPES.std, CARD_TYPES.invitation];
export type BeforeTheDayCardType = typeof CARD_TYPES_BEFORE_THE_DAY[number];

export const CARD_TYPES_ON_THE_DAY = [
  CARD_TYPES.place,
  CARD_TYPES.tableNumber,
  CARD_TYPES.menu,
  CARD_TYPES.program,
];
export type OnTheDayCardType = typeof CARD_TYPES_ON_THE_DAY[number];

export const CARD_TYPES_AFTER_THE_DAY = [CARD_TYPES.thankyou];
export type AfterTheDayCardType = typeof CARD_TYPES_AFTER_THE_DAY[number];
